import React, { useEffect, useState, useRef } from 'react'
import { Col, Form, Input, Row, Select } from 'antd'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import UploadDocument from 'components/global/UploadDocument'
import { partnerContentStore } from 'state/setting/partnerContent'
import { validateUrl } from 'helper/utility'
import { connect } from 'react-redux'
import '../styles/main.scss'
import {
  useAddPartnerSlide,
  useUpdatePartnerSlide,
} from 'api/Settings/PartnerContent'
import { X } from '@phosphor-icons/react'

const quillModules = {
  toolbar: [['bold', 'italic', 'underline'], [{ color: ['#F2994A', '#000'] }]],
}

const quillFormats = ['bold', 'italic', 'underline', 'color']

const smallQuillModules = {
  toolbar: [['bold', 'italic', 'underline'], [{ color: ['#F2994A', '#000'] }]],
}

interface PartnersEditorProps {
  fileData: any
  multipleFileData: any[]
  loading: boolean
}

const PartnersEditor: React.FC<PartnersEditorProps> = ({
  fileData,
  multipleFileData,
  loading,
}) => {
  const [form] = Form.useForm()
  const { slides, currentSlide, updateSlide } = partnerContentStore()

  const current = slides[currentSlide]

  const [coverPhotosIds, setCoverPhotosIds] = useState<string[]>(
    current?.coverPhotosIds || [],
  )
  const [coverPhotoPreviewUrl, setCoverPhotoPreviewUrl] = useState<string>('')
  const isUpdatingFromStore = useRef(false)
  const isAddingButton = useRef(false)

  const { mutate: addPartnerSlide, isLoading: addPartnerLoading } =
    useAddPartnerSlide()
  const { mutate: updatePartnerSlide, isLoading: updatePartnerLoading } =
    useUpdatePartnerSlide()

  // Sync form and local state from store when slide changes
  useEffect(() => {
    if (!current) return
    isUpdatingFromStore.current = true

    form.setFieldsValue({
      title: current.title,
      content: current.content,
      buttons:
        current.buttons && current.buttons.length > 0
          ? current.buttons
          : [{ text: '', link: '' }],
      layout: current.layout || 'left',
    })
    setCoverPhotosIds(current.coverPhotosIds || [])
    setCoverPhotoPreviewUrl(current.coverPhotoPreviewUrl || '')

    // Reset flag after state updates
    setTimeout(() => {
      isUpdatingFromStore.current = false
    }, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide, current?.id])

  // Update store only when local state changes (not when syncing from store)
  useEffect(() => {
    if (!current || isUpdatingFromStore.current) return

    const currentIds = current.coverPhotosIds || []
    const currentPreview = current.coverPhotoPreviewUrl || ''

    // Only update if values actually changed
    if (
      JSON.stringify(coverPhotosIds) !== JSON.stringify(currentIds) ||
      coverPhotoPreviewUrl !== currentPreview
    ) {
      updateSlide(current.id, { coverPhotosIds, coverPhotoPreviewUrl })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverPhotosIds, coverPhotoPreviewUrl])

  // Handle file upload completion and extract preview URL
  useEffect(() => {
    if (!fileData?._id || !current || isUpdatingFromStore.current) return

    // Check if this file is already in our IDs
    const isNewFile = !coverPhotosIds.includes(fileData._id)

    if (isNewFile) {
      // Get preview URL from fileData if available
      const previewUrl = fileData.link || fileData.thumb || fileData.post || fileData.original
      if (previewUrl) {
        setCoverPhotoPreviewUrl(previewUrl)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileData?._id])

  const handleDocumentIdsChange = (ids: string[]) => {
    // Ensure only single file upload - keep only the most recent ID
    const singleId = ids.length > 0 ? [ids[ids.length - 1]] : []
    setCoverPhotosIds(singleId)
    // Clear preview URL when image is removed
    if (singleId.length === 0) {
      setCoverPhotoPreviewUrl('')
    }
  }

  const ensureAtLeastOneButton = () => {
    const buttons = form.getFieldValue('buttons') || []
    const normalized = buttons.length > 0 ? buttons : [{ text: '', link: '' }]
    form.setFieldsValue({ buttons: normalized })
    if (current) {
      updateSlide(current.id, { buttons: normalized })
    }
  }

  useEffect(() => {
    ensureAtLeastOneButton()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!current) {
    return (
      <div className="text-16 w-400 text-gray">
        No partner section selected. Please add a section.
      </div>
    )
  }

  const handleFinish = (values: any) => {
    if (!current) return
    const payload = {
      title: values.title || '',
      content: values.content || '',
      buttons: values.buttons || [],
      layout: values.layout || 'left',
      coverPhotosIds,
      coverPhotoPreviewUrl,
    }

    const isLocal = String(current.id).includes('local-')
    if (isLocal) {
      addPartnerSlide(payload as any, {
        onSuccess: (data: any) => {
          if (data?._id) {
            updateSlide(current.id, { id: data._id })
          }
        },
      })
    } else {
      updatePartnerSlide(
        { payload: payload as any, slideId: String(current.id) },
        {
          onSuccess: (data: any) => {
            if (data?._id) {
              updateSlide(current.id, { id: data._id })
            }
          },
        },
      )
    }
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          title: current.title,
          content: current.content,
          buttons:
            current.buttons && current.buttons.length > 0
              ? current.buttons
              : [{ text: '', link: '' }],
          layout: current.layout || 'left',
        }}
      >
        <Row>
          <Col span={24}>
            <label className="input-label">Layout</label>
            <Form.Item name="layout" className="form-control">
              <Select
                className="h-50px select-field"
                placeholder="Select layout"
                onChange={value => {
                  form.setFieldsValue({ layout: value })
                  if (current && !isUpdatingFromStore.current) {
                    updateSlide(current.id, {
                      layout: value,
                    })
                  }
                }}
              >
                <Select.Option value="left">Left</Select.Option>
                <Select.Option value="right">Right</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <label className="input-label">Title</label>
            <Form.Item name="title" className="form-control">
              <ReactQuill
                theme="snow"
                modules={smallQuillModules}
                formats={quillFormats}
                placeholder="Enter text"
                className="small-quill-editor"
                onChange={value => {
                  form.setFieldsValue({ title: value })
                  if (current && !isUpdatingFromStore.current) {
                    updateSlide(current.id, {
                      title: value,
                    })
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <label className="input-label">Content</label>
            <Form.Item name="content" className="form-control">
              <ReactQuill
                theme="snow"
                modules={quillModules}
                formats={quillFormats}
                placeholder="Partner description"
                onChange={value => {
                  form.setFieldsValue({ content: value })
                  if (current && !isUpdatingFromStore.current) {
                    updateSlide(current.id, {
                      content: value,
                    })
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row className="mt-16px">
          <Col span={24}>
            <label className="input-label">Cover photo</label>
            <UploadDocument
              documentIds={coverPhotosIds}
              setDocumentIds={handleDocumentIdsChange}
              multiple={false}
              docType="image/*"
              soruceType="other"
              files={
                coverPhotoPreviewUrl
                  ? [
                      {
                        _id: coverPhotosIds[0] || 'cover-photo',
                        status: 'active',
                        ext: '.jpeg',
                        name: 'Cover photo',
                        original: coverPhotoPreviewUrl,
                        post: coverPhotoPreviewUrl,
                        thumb: coverPhotoPreviewUrl,
                      },
                    ]
                  : []
              }
            />
          </Col>
        </Row>

        <Row className="mt-20px">
          <Col span={24}>
            <h4 className="text-16 w-600">Call to action</h4>
          </Col>
        </Row>

        <Form.List name="buttons">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, idx) => (
                <Row gutter={[16, 16]} className="mt-10px" key={field.key}>
                  <Col span={fields.length > 1 ? 11 : 12}>
                    <label className="input-label">Call to action</label>
                    <Form.Item
                      name={[field.name, 'text']}
                      className="form-control"
                    >
                      <Input
                        placeholder="Donate Today"
                        className="form-control-field"
                        onChange={() => {
                          if (isAddingButton.current) return
                          const currentButtons =
                            form.getFieldValue('buttons') || []
                          if (current && !isUpdatingFromStore.current) {
                            updateSlide(current.id, {
                              buttons: currentButtons,
                            })
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={fields.length > 1 ? 11 : 12}>
                    <label className="input-label">Link</label>
                    <Form.Item
                      name={[field.name, 'link']}
                      className="form-control"
                      rules={[{ validator: validateUrl }]}
                    >
                      <Input
                        placeholder="www.example.com"
                        className="form-control-field"
                        onChange={() => {
                          if (isAddingButton.current) return
                          const currentButtons =
                            form.getFieldValue('buttons') || []
                          if (current && !isUpdatingFromStore.current) {
                            updateSlide(current.id, {
                              buttons: currentButtons,
                            })
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  {fields.length > 1 && (
                    <Col span={2} className="flex align-center">
                      <button
                        type="button"
                        onClick={() => {
                          remove(field.name)
                          setTimeout(() => {
                            const currentButtons =
                              form.getFieldValue('buttons') || []
                            if (current && !isUpdatingFromStore.current) {
                              updateSlide(current.id, {
                                buttons: currentButtons,
                              })
                            }
                          }, 0)
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginTop: '24px',
                        }}
                      >
                        <X size={20} color="#ff4d4f" weight="bold" />
                      </button>
                    </Col>
                  )}
                </Row>
              ))}
              <Row className="mt-10px">
                <Col span={24}>
                  <button
                    type="button"
                    onClick={() => {
                      isAddingButton.current = true
                      add()
                      setTimeout(() => {
                        isAddingButton.current = false
                        const currentButtons =
                          form.getFieldValue('buttons') || []
                        if (current && !isUpdatingFromStore.current) {
                          updateSlide(current.id, {
                            buttons: currentButtons,
                          })
                        }
                      }, 100)
                    }}
                    className="bordered-prim-btn"
                  >
                    Add call to action
                  </button>
                </Col>
              </Row>
            </>
          )}
        </Form.List>

        <Row className="mt-20px">
          <button
            className="prim-btn"
            type="submit"
            disabled={addPartnerLoading || updatePartnerLoading}
          >
            {addPartnerLoading || updatePartnerLoading ? 'Saving...' : 'Save'}
          </button>
        </Row>
      </Form>
    </>
  )
}

const mapStateToProps = (state: any) => ({
  fileData: state.fileUpload.fileData,
  multipleFileData: state.fileUpload.multipleFileData,
  loading: state.fileUpload.loading,
})

export default connect(mapStateToProps)(PartnersEditor)
